import * as React from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import 'react-day-picker/style.css';
import './date-range-picker.css';
import { Button } from '@/components/ui/button';
import * as Popover from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface Props {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}

function toISO(d: Date) { return format(d, 'yyyy-MM-dd'); }

interface Preset { label: string; build: () => { from: string; to: string } }

const PRESETS: Preset[] = [
  { label: 'Today',       build: () => { const t = new Date(); return { from: toISO(t), to: toISO(t) }; } },
  { label: 'Yesterday',   build: () => { const t = new Date(); t.setDate(t.getDate() - 1); return { from: toISO(t), to: toISO(t) }; } },
  { label: 'Last 7 days', build: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); return { from: toISO(s), to: toISO(e) }; } },
  { label: 'Last 30 days',build: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); return { from: toISO(s), to: toISO(e) }; } },
  { label: 'This month',  build: () => { const t = new Date(); const s = new Date(t.getFullYear(), t.getMonth(), 1); return { from: toISO(s), to: toISO(t) }; } },
  { label: 'Last month',  build: () => { const t = new Date(); const s = new Date(t.getFullYear(), t.getMonth() - 1, 1); const e = new Date(t.getFullYear(), t.getMonth(), 0); return { from: toISO(s), to: toISO(e) }; } },
  { label: 'This year',   build: () => { const t = new Date(); const s = new Date(t.getFullYear(), 0, 1); return { from: toISO(s), to: toISO(t) }; } },
];

function useIsMobile(breakpointPx = 640) {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(`(max-width: ${breakpointPx - 1}px)`).matches,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpointPx]);
  return isMobile;
}

export function DateRangePicker({ from, to, onChange }: Props) {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();

  const propRange = React.useMemo<DateRange | undefined>(
    () => (from && to ? { from: parseISO(from), to: parseISO(to) } : undefined),
    [from, to],
  );
  const [pending, setPending] = React.useState<DateRange | undefined>(propRange);

  React.useEffect(() => { if (open) setPending(propRange); }, [open, propRange]);

  const commit = React.useCallback((r: DateRange | undefined) => {
    if (r?.from && r?.to) onChange({ from: toISO(r.from), to: toISO(r.to) });
  }, [onChange]);

  const matchesPreset = React.useCallback((label: string) => {
    if (!from || !to) return false;
    const p = PRESETS.find((x) => x.label === label)?.build();
    return !!p && p.from === from && p.to === to;
  }, [from, to]);

  const triggerLabel = from && to
    ? from === to
      ? format(parseISO(from), 'd MMM yyyy')
      : `${format(parseISO(from), 'd MMM')} – ${format(parseISO(to), 'd MMM yyyy')}`
    : 'Select dates';

  return (
    <Popover.Root
      open={open}
      // Closing without explicit Apply discards pending edits — user must click
      // Apply (or pick a preset) to commit. Avoids accidental commits on
      // outside-click.
      onOpenChange={(o) => setOpen(o)}
    >
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          className={cn(
            'group h-10 w-full max-w-full justify-between gap-2 px-3 font-normal tabular-nums sm:w-auto sm:min-w-[260px]',
            'transition-all hover:border-primary/40 hover:bg-muted/40',
            open && 'border-primary/60 ring-2 ring-primary/15',
          )}
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <span className="text-foreground/90">{triggerLabel}</span>
          </span>
          <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} strokeWidth={2} />
        </Button>
      </Popover.Trigger>

      <Popover.Content
        align="start"
        sideOffset={8}
        collisionPadding={8}
        className={cn(
          'p-0 w-[min(640px,calc(100vw-1rem))] sm:w-auto sm:min-w-[640px] sm:max-w-[calc(100vw-2rem)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        )}
      >
        <div className="flex flex-col sm:flex-row">
          {/* Preset rail — horizontal chips on mobile, vertical sidebar on sm+ */}
          <div className="flex shrink-0 flex-row gap-1 overflow-x-auto border-b bg-muted/30 px-2 py-2 sm:w-[150px] sm:flex-col sm:gap-0 sm:overflow-visible sm:border-b-0 sm:border-r sm:px-0 sm:py-3">
            <p className="hidden px-3 pb-1 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block">Quick</p>
            {PRESETS.map((p) => {
              const active = matchesPreset(p.label);
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    const next = p.build();
                    onChange(next);
                    setPending({ from: parseISO(next.from), to: parseISO(next.to) });
                    setOpen(false);
                  }}
                  className={cn(
                    'shrink-0 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-[0.8125rem] transition-colors',
                    'sm:mx-1.5 sm:my-0.5',
                    active
                      ? 'bg-primary/12 font-medium text-primary'
                      : 'text-foreground/80 hover:bg-muted hover:text-foreground',
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Calendar pane */}
          <div className="flex flex-col p-3 sm:p-4">
            <DayPicker
              mode="range"
              numberOfMonths={isMobile ? 1 : 2}
              selected={pending}
              defaultMonth={pending?.from ?? new Date()}
              // Custom tri-state logic (overrides react-day-picker's default
              // range behaviour, which extends a completed range backward when
              // a 3rd date is clicked):
              //   1st click  → start (from = day, to = undefined)
              //   2nd click  → end (or swap if before from)
              //   3rd click  → reset to a fresh start
              // Apply is the only commit path; popup stays open until Apply.
              onSelect={(_r, day) => {
                setPending((prev) => {
                  if (!prev?.from) return { from: day, to: undefined };
                  if (prev.from && !prev.to) {
                    if (day.getTime() === prev.from.getTime()) return { from: day, to: day };
                    if (day < prev.from) return { from: day, to: prev.from };
                    return { from: prev.from, to: day };
                  }
                  return { from: day, to: undefined };
                });
              }}
            />

            {/* Status + actions */}
            <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                {!pending?.from && <span>Pick a start date</span>}
                {pending?.from && !pending.to && (
                  <>
                    <span className="font-medium text-foreground tabular-nums">{format(pending.from, 'd MMM yyyy')}</span>
                    <span>→ pick end date</span>
                  </>
                )}
                {pending?.from && pending.to && (
                  <span className="tabular-nums">
                    <span className="font-medium text-foreground">{format(pending.from, 'd MMM')}</span>
                    <span className="mx-1.5">→</span>
                    <span className="font-medium text-foreground">{format(pending.to, 'd MMM yyyy')}</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setPending(undefined); }} className="text-muted-foreground hover:text-foreground" disabled={!pending?.from}>
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={() => { commit(pending); setOpen(false); }}
                  disabled={!pending?.from || !pending?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
